/**
 * Fin de sesión API/cliente: motivos SESSION_* y storage para Alert en login.
 */

export const USUARIO_STORAGE_KEY = 'ch-cardiologia-usuario';
export const SESSION_END_STORAGE_KEY = 'ch-cardiologia-session-end';
export const SESSION_TIMEOUT_MS = 60 * 60 * 1000; // 1 hora

export const SESSION_CODES = {
    EXPIRED_INACTIVITY: 'SESSION_EXPIRED_INACTIVITY',
    SUPERSEDED: 'SESSION_SUPERSEDED',
    CLOSED: 'SESSION_CLOSED',
};

const ALERT_CODES = new Set(Object.values(SESSION_CODES));

const FALLBACK_MESSAGES = {
    [SESSION_CODES.EXPIRED_INACTIVITY]: 'Tu sesión venció por inactividad. Volvé a iniciar sesión.',
    [SESSION_CODES.SUPERSEDED]: 'Tu sesión se cerró porque esta cuenta inició sesión en otro dispositivo o navegador.',
    [SESSION_CODES.CLOSED]: 'Tu sesión fue cerrada. Volvé a iniciar sesión.',
};

export const SESSION_END_TITLES = {
    [SESSION_CODES.EXPIRED_INACTIVITY]: 'Sesión vencida por inactividad',
    [SESSION_CODES.SUPERSEDED]: 'Sesión iniciada en otro lugar',
    [SESSION_CODES.CLOSED]: 'Sesión cerrada',
};

let sessionEndHandled = false;

export function resetSessionEndGuard() {
    sessionEndHandled = false;
}

export function clearSessionEndNotice() {
    try {
        window.sessionStorage.removeItem(SESSION_END_STORAGE_KEY);
    } catch (_) {
        // ignore
    }
}

export function peekSessionEndNotice() {
    try {
        const raw = window.sessionStorage.getItem(SESSION_END_STORAGE_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch (_) {
        return null;
    }
}

export function consumeSessionEndNotice() {
    const notice = peekSessionEndNotice();
    clearSessionEndNotice();
    return notice;
}

export function writeSessionEndNotice(code, message) {
    if (!ALERT_CODES.has(code)) return;
    try {
        window.sessionStorage.setItem(
            SESSION_END_STORAGE_KEY,
            JSON.stringify({
                code,
                message: message || FALLBACK_MESSAGES[code] || 'Volvé a iniciar sesión.',
            })
        );
    } catch (_) {
        // ignore
    }
}

export function touchUsuarioActivity() {
    try {
        const raw = window.localStorage.getItem(USUARIO_STORAGE_KEY);
        if (!raw) return;
        const usuario = JSON.parse(raw);
        if (!usuario || !usuario.token) return;
        usuario.date = new Date().toString();
        window.localStorage.setItem(USUARIO_STORAGE_KEY, JSON.stringify(usuario));
    } catch (_) {
        // ignore
    }
}

function isOnLoginRoute() {
    const currentPath = window.location.hash || window.location.pathname || '';
    return currentPath.includes('/login');
}

function isAuthLoginRequest(requestName) {
    return String(requestName || '').replace(/^\//, '') === 'auth/login';
}

function legacyTokenMessage(message) {
    if (!message) return false;
    const errorLower = String(message).toLowerCase();
    return errorLower.includes('token vencido')
        || errorLower.includes('token inválido')
        || errorLower.includes('token invalido')
        || errorLower.includes('token requerido')
        || errorLower.includes('unauthorized')
        || errorLower.includes('sesión expirada');
}

/**
 * Procesa un 401/cierre de sesión. Devuelve el mensaje a mostrar en response.error.
 * @param {{ status?: number, code?: string, message?: string, requestName?: string }} payload
 * @returns {string|null} mensaje de sesión, o null si no aplica expulsión
 */
export function handleSessionEnd(payload = {}) {
    const { status, code, message, requestName } = payload;

    if (isAuthLoginRequest(requestName)) {
        return null;
    }

    if (isOnLoginRoute()) {
        return null;
    }

    const isSessionCode = ALERT_CODES.has(code);
    const isTokenRequiredOrInvalid = code === 'TOKEN_REQUIRED' || code === 'TOKEN_INVALID';
    const legacy = !code && (status === 401 || legacyTokenMessage(message));

    if (!isSessionCode && !isTokenRequiredOrInvalid && !legacy) {
        return null;
    }

    const resolvedCode = isSessionCode
        ? code
        : (legacy ? SESSION_CODES.EXPIRED_INACTIVITY : null);

    const resolvedMessage = message
        || (resolvedCode ? FALLBACK_MESSAGES[resolvedCode] : null)
        || 'Volvé a iniciar sesión.';

    if (sessionEndHandled) {
        return resolvedMessage;
    }
    sessionEndHandled = true;

    if (resolvedCode && ALERT_CODES.has(resolvedCode)) {
        writeSessionEndNotice(resolvedCode, resolvedMessage);
    } else {
        clearSessionEndNotice();
    }

    window.localStorage.removeItem(USUARIO_STORAGE_KEY);

    if (!isOnLoginRoute()) {
        window.location.hash = '#/login';
    }

    return resolvedMessage;
}

/**
 * Expulsión local por inactividad (AuthContext).
 */
export function endSessionLocalInactivity() {
    writeSessionEndNotice(
        SESSION_CODES.EXPIRED_INACTIVITY,
        FALLBACK_MESSAGES[SESSION_CODES.EXPIRED_INACTIVITY]
    );
    window.localStorage.removeItem(USUARIO_STORAGE_KEY);
    if (!isOnLoginRoute()) {
        window.location.hash = '#/login';
    }
}
